<template>
  <div class="carousel" ref="carousel">
    <div class="carousel-inner" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
      <div v-for="(slide, index) in slides" :key="index" class="carousel-slide">
        <div class="slide-content">
          <h1 v-if="slide.title">{{ slide.title }}</h1>
          <h2 v-if="slide.subTitle">{{ slide.subTitle }}</h2>
          <p v-if="slide.description">{{ slide.description }}</p>
          <router-link v-if="slide.buttonText && slide.buttonLink" :to="slide.buttonLink" class="btn btn-primary">
            {{ slide.buttonText }}
          </router-link>
        </div>
        <template v-if="isVideo(slide.image)">
          <video :src="slide.image" class="slide-image" autoplay loop muted playsinline></video>
        </template>
        <img v-else :src="slide.image" :alt="slide.title" class="slide-image" />
      </div>
    </div>
    <button class="carousel-control prev" @click="prevSlide">
      <font-awesome-icon icon="chevron-left" />
    </button>
    <button class="carousel-control next" @click="nextSlide">
      <font-awesome-icon icon="chevron-right" />
    </button>
    <div class="carousel-indicators">
      <button
        v-for="(slide, index) in slides"
        :key="index"
        :class="{ active: index === currentIndex }"
        @click="goToSlide(index)"
      ></button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

interface Slide {
  image: string;
  title?: string;
  subTitle?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
}

const props = defineProps<{
  slides: Slide[];
  autoPlay?: boolean;
  interval?: number;
}>();

const emit = defineEmits(['click']);

const isVideo = (url: string) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg'];
  return videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
};

const currentIndex = ref(0);
const carousel = ref<HTMLElement | null>(null);
const timer = ref<NodeJS.Timeout | null>(null);

const goToSlide = (index: number) => {
  // 如果点击的是当前激活的指示器，则不执行任何操作
  if (index === currentIndex.value) {
    return;
  }
  
  // 清除定时器
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
  
  // 更新当前索引
  currentIndex.value = index;
  
  // 如果启用了自动播放，则重新启动定时器
  if (props.autoPlay) {
    // 使用setTimeout确保在点击事件处理完成后再启动新的定时器
    setTimeout(() => {
      startTimer();
    }, 0);
  }
};

const startTimer = () => {
  // 确保先清除可能存在的定时器
  if (timer.value) {
    clearInterval(timer.value);
  }
  // 设置新的定时器
  timer.value = setInterval(nextSlide, props.interval || 3000);
};

const nextSlide = () => {
  // 清除定时器
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
  
  // 更新索引
  currentIndex.value = (currentIndex.value + 1) % props.slides.length;
  
  // 如果启用了自动播放，则重新启动定时器
  if (props.autoPlay) {
    startTimer();
  }
};

const prevSlide = () => {
  // 清除定时器
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
  
  // 更新索引
  currentIndex.value = (currentIndex.value - 1 + props.slides.length) % props.slides.length;
  
  // 如果启用了自动播放，则重新启动定时器
  if (props.autoPlay) {
    startTimer();
  }
};

onMounted(() => {
  if (props.autoPlay) {
    startTimer();
    
    carousel.value?.addEventListener('mouseenter', () => {
      if (timer.value) {
        clearInterval(timer.value);
        timer.value = null;
      }
    });
    
    carousel.value?.addEventListener('mouseleave', () => {
      if (props.autoPlay) {
        startTimer();
      }
    });
  }
});

onUnmounted(() => {
  // 清除定时器
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
});
</script>

<style scoped>
.carousel {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
  max-width: 2000px;
  margin: 0 auto;
}

.carousel-inner {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.5s ease-in-out;
}

.carousel-slide {
  min-width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-content {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(-40%, -50%);
  text-align: center;
  z-index: 2;

}
.slide-content h1 {
  color: #fb6e01;
  font-size: 70px;
}
.slide-content h2 {
  color: #000000;
  font-size: 30px;
  font-weight: normal;
}
.slide-content p {
  color: #000000;
  font-size: 16px;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  padding: 10px;
  cursor: pointer;
  z-index: 3;
}

.carousel-control.prev {
  left: 20px;
}

.carousel-control.next {
  right: 20px;
}

.carousel-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 3;
}

.carousel-indicators button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: none;
  cursor: pointer;
  transition: background 0.3s;
}

.carousel-indicators button.active {
  background: white;
}
@media (max-width: 768px) {
  .carousel {
    position: relative;
    width: 100%;
    height: 153px;
    /* height: 260px; */
    overflow: hidden;
  }
  .slide-content {
    right: 50%;
    transform: translate(50%, -50%);

}
  .slide-content h1 {
    font-size: 20px;
  }
.slide-content h2 {
    font-size: 16px;
}
.slide-content p {
    font-size: 12px;
}
}
</style>
